import React from "react";
import { AmplifyLogo } from "@/components/AmplifyLogo";
import { UnsavedIndicator } from "./DraftBackupUI";
import {
  Calculator, FileText, Settings, Plus, Save, ChevronRight,
  LogOut, User, LayoutDashboard, Copy
} from "lucide-react";

export function Header({
  user,
  isAdmin,
  logout,
  page,
  setPage,
  saveStatus,
  saveQuote,
  saveAsNewVersion,
  saveAndNext,
  onNavigateToDashboard,
  newQuote,
  lineItems,
  hasExceededDiscounts,
  currentQuoteId,
  setShowNewQuoteConfirm,
  hasUnsavedChanges,
}) {
  // Tab configuration - Admin tab only visible to admins
  const tabs = [
    { id: 1, label: "Quote Builder", icon: Calculator },
    { id: 2, label: "Proposal", icon: FileText },
    ...(isAdmin ? [{ id: 3, label: "Admin", icon: Settings }] : []),
  ];

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50 print:hidden" data-testid="header">
      <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <AmplifyLogo size={32} />
          <span className="text-xl font-semibold font-['Outfit'] text-slate-900 tracking-tight">Amplify HR</span>
          <span className="text-xs font-medium text-slate-400 border border-slate-200 rounded-full px-2.5 py-0.5 ml-1">Pricing Engine</span>
        </div>

        {/* Navigation Tabs */}
        <nav className="flex items-center gap-1" data-testid="nav-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`nav-tab-${tab.id}`}
              onClick={() => setPage(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                page === tab.id
                  ? "bg-[#3c68ff] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {/* Unsaved Changes Indicator */}
          <UnsavedIndicator visible={hasUnsavedChanges} />
          
          {/* User Info */}
          <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#3c68ff]/10 text-[#3c68ff]">
              <User size={16} />
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-slate-800" data-testid="user-name">{user?.full_name}</div>
              <div className="text-xs text-slate-400" data-testid="user-role">{isAdmin ? "Admin" : "User"}</div>
            </div>
          </div>
          
          {saveStatus && (
            <span 
              className={`text-sm font-medium ${saveStatus.includes("exceeded") || saveStatus.includes("failed") ? "text-red-600" : "text-emerald-600"}`} 
              data-testid="save-status"
            >
              {saveStatus}
            </span>
          )}
          
          <button
            data-testid="save-quote-btn"
            onClick={saveQuote}
            disabled={lineItems.length === 0 || hasExceededDiscounts}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#3c68ff] text-white rounded-lg text-sm font-medium hover:bg-[#2b4bca] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <Save size={15} />
            Save
          </button>
          
          {currentQuoteId && (
            <button
              data-testid="save-as-new-version-btn"
              onClick={saveAsNewVersion}
              disabled={lineItems.length === 0 || hasExceededDiscounts}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              title="Save as new version"
            >
              <Copy size={15} />
              New Version
            </button>
          )}
          
          <button
            data-testid="save-and-next-btn"
            onClick={saveAndNext}
            disabled={lineItems.length === 0 || hasExceededDiscounts}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            title="Save and go to Proposal"
          >
            <ChevronRight size={15} />
            Save & Next
          </button>
          
          <button
            data-testid="dashboard-btn"
            onClick={onNavigateToDashboard}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all"
            title="View Dashboard"
          >
            <LayoutDashboard size={15} />
          </button>
          
          <button
            data-testid="new-quote-btn"
            onClick={() => lineItems.length > 0 ? setShowNewQuoteConfirm(true) : newQuote()}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all"
          >
            <Plus size={15} />
            New
          </button>
          
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-2 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg text-sm font-medium transition-all"
            title="Sign out"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </header>
  );
}
